// Вкладка «Склад» раздела «Себестоимость» (только при подключённом iikoCloud): складские отчёты на остатках iikoCloud.
// Первый отчёт — «Минусовые остатки»; второй («Цены поставщиков») встанет в REPORTS, и над отчётом появится
// переключатель подвкладок — пока отчёт один, вместо него заголовок.
//
// Минусы — снимок «сейчас» (/data/stock/negative): бэкенд кеширует его на 20 минут и при сбое iikoCloud отдаёт
// последний снимок не старше 2 часов (stale) — тогда явная пометка «данные на HH:MM, iikoCloud сейчас недоступен».
// Ресторан в шапке — как на «Кухне»: рестораны без iikoCloud выбрать нельзя. Фильтр по складу — на устройстве.
// Продажи текущей смены iiko списывает при её закрытии, поэтому днём минусы их ещё не учитывают — об этом подпись.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, Menu, MenuItem, Skeleton, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SegmentedTabs from '../../components/SegmentedTabs';
import StickyFiltersBar from '../../components/StickyFiltersBar';
import FiltersTopRow from '../../components/FiltersTopRow';
import FadeIn from '../../components/motion/FadeIn';
import GroupSelector from '../Metrics/components/GroupSelector';
import { DESKTOP_HEADER_HEIGHT, MOBILE_TOP_INSET, useDesktopLayout } from '../../components/layout/AppLayout';
import { fetchStockNegative } from '../../services/dataService';
import { CARD_LABEL_COLOR, CARD_RADIUS, CARD_SURFACE_SX, DIVIDER_COLOR, MUTED_TEXT_COLOR, SECTION_ACCENTS } from '../../styles/tokens';
import { selectionTitle } from '../../utils/restaurant';
import { STALE_COLOR, clockTime } from '../Kitchen/kitchenFormat';
import { cacheKey, readCache, writeCache } from '../Kitchen/kitchenCache';
import { useKitchenRestaurants } from '../Kitchen/useKitchenRestaurants';
import { useVisiblePolling } from '../Kitchen/useVisiblePolling';
import StockItemPanel, { PANEL_WIDTH } from './components/stock/StockItemPanel';
import { NEGATIVE_COLOR } from './components/stock/StockHistoryChart';
import {
  ALL_STORES,
  effectiveStore,
  formatCost,
  formatQuantity,
  NO_PRICE_GROUP,
  noPriceText,
  positionsText,
  storeFilterKind,
  visibleStores,
  withNoPriceGroup,
} from './utils/stockModel';

const ACCENT = SECTION_ACCENTS['/cost'];
const POLL_MS = 10 * 60 * 1000;
// Складские отчёты; больше одного — переключатель подвкладок вместо заголовка
const REPORTS = [{ value: 'negative', label: 'Минусовые остатки' }];

function StorePicker({ options, value, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const current = options.find((o) => o.id === value);
  return (
    <>
      <ButtonBase
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        sx={{
          minHeight: { xs: 44, md: 36 },
          px: 1.5,
          gap: 0.5,
          borderRadius: '10px',
          maxWidth: '100%',
          fontSize: '14px',
          fontWeight: 600,
          color: '#fff',
          backgroundColor: 'rgba(255,255,255,0.06)',
          '&.Mui-focusVisible': { outline: `2px solid ${ACCENT.accent}`, outlineOffset: '2px' },
        }}
      >
        <Box component="span" sx={{ color: MUTED_TEXT_COLOR, fontWeight: 500 }}>Склад:</Box>
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{current ? current.name : 'Все'}</Box>
        <KeyboardArrowDownIcon fontSize="small" aria-hidden="true" />
      </ButtonBase>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} slotProps={{ paper: { sx: { maxHeight: 420 } } }}>
        {[{ id: ALL_STORES, name: 'Все склады' }, ...options].map((option) => (
          <MenuItem
            key={option.id}
            selected={option.id === value}
            onClick={() => {
              onChange(option.id);
              setAnchor(null);
            }}
            sx={{ minHeight: 44 }}
          >
            {option.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function StoreFilter({ options, value, onChange }) {
  const kind = storeFilterKind(options);
  if (kind === 'none') return null;
  if (kind === 'menu') return <StorePicker options={options} value={value} onChange={onChange} />;
  return (
    <SegmentedTabs
      variant="filter"
      fullWidth={false}
      tabs={[{ value: ALL_STORES, label: 'Все склады' }, ...options.map((o) => ({ value: o.id, label: o.name }))]}
      value={value}
      onChange={onChange}
      label="Склад"
    />
  );
}

function AsOf({ data }) {
  if (!data?.updated_at) return null;
  const time = clockTime(data.updated_at);
  if (data.stale) {
    return (
      <Typography role="status" sx={{ fontSize: '13px', color: STALE_COLOR }}>
        Данные на {time}, iikoCloud сейчас недоступен
      </Typography>
    );
  }
  return (
    <Typography sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR }}>
      По состоянию на {time}; продажи текущей смены учтутся после её закрытия
    </Typography>
  );
}

function Notice({ children }) {
  return (
    <Box role="status" sx={{ ...CARD_SURFACE_SX, p: 3, textAlign: 'center' }}>
      <Typography sx={{ color: '#e4e4e7' }}>{children}</Typography>
    </Box>
  );
}

// Итог на месте баннера «Себестоимости»
function Summary({ totals }) {
  return (
    <Box sx={{ ...CARD_SURFACE_SX, p: { xs: 2, md: 2.5 } }}>
      <Typography sx={{ fontSize: { xs: '17px', md: '20px' }, fontWeight: 600, color: '#fff' }}>
        {positionsText(totals.items)} в минусе на сумму{' '}
        <Box component="span" sx={{ color: NEGATIVE_COLOR, fontWeight: 700, whiteSpace: 'nowrap' }}>{formatCost(totals.cost_sum)}</Box>
        {totals.no_price > 0 && (
          <Box component="span" sx={{ color: CARD_LABEL_COLOR, fontWeight: 500 }}>, {noPriceText(totals)}</Box>
        )}
      </Typography>
    </Box>
  );
}

// Иерархия «склад → группа → позиция» — формой, а не цветом названий: шапка склада — светлая полоса с иконкой,
// группа — строка на подложке темнее шапки (прилипает к верху при прокрутке), позиции — с отступом слева.
// Фоны непрозрачные: прилипший заголовок группы закрывает строки под собой
const STORE_HEADER_BG = '#303035';
const GROUP_HEADER_BG = '#29292d';
const GROUP_HEADER_HOVER = '#2d2d32';

// Название — в одну строку с многоточием, полностью — по наведению (title) и в панели по нажатию
// selected — позиция открыта в панели справа (десктоп)
function ProductRow({ product, first, selected, onOpen }) {
  return (
    <ButtonBase
      onClick={onOpen}
      aria-label={`${product.name}: ${formatQuantity(product.quantity, product.unit)}, ${product.no_price ? 'нет цены' : formatCost(product.cost_sum)}`}
      aria-current={selected || undefined}
      sx={{
        ...(selected && { backgroundColor: `rgba(${ACCENT.rgb},0.12)` }),
        width: '100%',
        // Отступ слева на 16 px больше, чем у заголовка группы
        pl: 3,
        pr: 1,
        minHeight: 44,
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr) auto 16px', md: 'minmax(0, 1fr) 130px 110px 16px' },
        alignItems: 'center',
        gap: 1.5,
        textAlign: 'left',
        borderRadius: '8px',
        borderTop: first ? 'none' : `1px solid ${DIVIDER_COLOR}`,
        '@media (hover: hover)': { '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' } },
        '&.Mui-focusVisible': { outline: `2px solid ${ACCENT.accent}`, outlineOffset: '-2px' },
      }}
    >
      <Typography noWrap title={product.name} sx={{ fontSize: '14px', fontWeight: 400, color: '#fff' }}>{product.name}</Typography>
      {/* Телефон: количество над суммой; десктоп — колонками */}
      <Box sx={{ display: { xs: 'flex', md: 'contents' }, flexDirection: 'column', alignItems: 'flex-end' }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: NEGATIVE_COLOR, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', textAlign: 'right' }}>
          {formatQuantity(product.quantity, product.unit)}
        </Typography>
        {/* Без цены — не «0 ₽»: себестоимость неизвестна, а не нулевая */}
        <Typography sx={{ fontSize: { xs: '12px', md: '14px' }, color: product.no_price ? MUTED_TEXT_COLOR : CARD_LABEL_COLOR, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', textAlign: 'right' }}>
          {product.no_price ? 'нет цены' : formatCost(product.cost_sum)}
        </Typography>
      </Box>
      <KeyboardArrowRightIcon aria-hidden="true" sx={{ fontSize: 16, color: MUTED_TEXT_COLOR }} />
    </ButtonBase>
  );
}

const groupKey = (store, group) => `${store.id}:${group.id || group.name}`;

// Заголовок группы — кнопка: свернуть/развернуть. Число позиций и сумма — чтобы по свёрнутым было видно, где деньги.
// stickyTop — под шапкой (десктоп) или под закреплённой панелью фильтров (телефон)
function GroupHeader({ group, expanded, controls, stickyTop, onToggle }) {
  return (
    <ButtonBase
      onClick={onToggle}
      aria-expanded={expanded}
      aria-controls={controls}
      sx={{
        position: 'sticky',
        top: stickyTop,
        zIndex: 2,
        width: '100%',
        px: 1,
        minHeight: 40,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        textAlign: 'left',
        borderRadius: '8px',
        backgroundColor: GROUP_HEADER_BG,
        '@media (hover: hover)': { '&:hover': { backgroundColor: GROUP_HEADER_HOVER } },
        '&.Mui-focusVisible': { outline: `2px solid ${ACCENT.accent}`, outlineOffset: '-2px' },
      }}
    >
      <KeyboardArrowDownIcon
        aria-hidden="true"
        sx={{ fontSize: 18, flexShrink: 0, color: CARD_LABEL_COLOR, transition: 'transform 0.2s ease', transform: expanded ? 'none' : 'rotate(-90deg)' }}
      />
      <Box component="span" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', py: group.note ? 0.5 : 0 }}>
        <Typography component="span" noWrap title={group.name} sx={{ fontSize: '14px', fontWeight: 600, color: '#fff' }}>
          {group.name}
        </Typography>
        {group.note && (
          <Typography component="span" sx={{ fontSize: '12px', color: MUTED_TEXT_COLOR }}>{group.note}</Typography>
        )}
      </Box>
      <Typography component="span" sx={{ flexShrink: 0, fontSize: '13px', color: CARD_LABEL_COLOR, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
        {positionsText(group.items)}
        {/* У группы без цены суммы нет — она по нулю */}
        {!group.no_price_group && (
          <>
            {' · '}
            <Box component="span" sx={{ color: NEGATIVE_COLOR, fontWeight: 600 }}>{formatCost(group.cost_sum)}</Box>
          </>
        )}
      </Typography>
    </ButtonBase>
  );
}

// Группы по сумме минуса (самая «дорогая» сверху — так их отдаёт бэкенд); первой — «Не было приходов на склад»
// (withNoPriceGroup). По умолчанию развёрнуты она и первая обычная группа.
// expanded — переопределения пользователя { groupKey: bool }
function StoreBlock({ store: source, openedId, onOpen, expanded, onToggle, stickyTop }) {
  const store = withNoPriceGroup(source);
  const firstRegular = store.groups.findIndex((group) => group.id !== NO_PRICE_GROUP);
  return (
    <Box component="section" aria-label={store.name} sx={{ ...CARD_SURFACE_SX }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: { xs: 2, md: 2.5 },
          py: 1.5,
          backgroundColor: STORE_HEADER_BG,
          borderRadius: `${CARD_RADIUS} ${CARD_RADIUS} 0 0`,
          borderBottom: `1px solid ${DIVIDER_COLOR}`,
        }}
      >
        <Inventory2OutlinedIcon aria-hidden="true" sx={{ fontSize: 20, flexShrink: 0, color: ACCENT.accent }} />
        {/* Телефон: итог второй строкой под названием (в одну строку длинный итог съедал название); десктоп — справа */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, gap: { xs: 0.25, md: 1.5 } }}>
          <Typography component="h3" noWrap title={store.name} sx={{ flex: { md: 1 }, minWidth: 0, fontSize: '16px', fontWeight: 600, color: '#fff' }}>
            {store.name}
          </Typography>
          <Typography sx={{ flexShrink: 0, fontSize: '13px', color: CARD_LABEL_COLOR, whiteSpace: { md: 'nowrap' }, fontVariantNumeric: 'tabular-nums' }}>
            {[positionsText(store.items), noPriceText(store)].filter(Boolean).join(', ')} · {formatCost(store.cost_sum)}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ px: { xs: 1, md: 1.5 }, py: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {store.groups.map((group, groupIndex) => {
          const key = groupKey(store, group);
          const open = expanded[key] ?? (group.id === NO_PRICE_GROUP || groupIndex === firstRegular);
          const listId = `stock-group-${key}`;
          return (
            <Box key={key}>
              <GroupHeader group={group} expanded={open} controls={listId} stickyTop={stickyTop} onToggle={() => onToggle(key, !open)} />
              <Box id={listId} hidden={!open}>
                {open &&
                  group.products.map((product, index) => (
                    <ProductRow
                      key={product.product_id}
                      product={product}
                      first={index === 0}
                      selected={openedId === `${store.id}:${product.product_id}`}
                      onOpen={() => onOpen(store, group, product)}
                    />
                  ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function LoadingList() {
  return (
    <Box aria-busy="true" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Skeleton variant="rounded" height={64} />
      <Box sx={{ ...CARD_SURFACE_SX, p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={40} />)}
      </Box>
    </Box>
  );
}

// tabs — переключатель «Обзор | ABC-анализ | Склад» из CostPage: в закреплённой панели, как на других вкладках
export default function StockTab({ selectedGroup, allGroups, activeServerName, setSelectedGroup, available, tabs = null }) {
  const desktop = useDesktopLayout();
  const { filter, unavailable } = useKitchenRestaurants({ available, selectedGroup, allGroups });
  const restaurant = filter.restaurant || null;
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [store, setStore] = useState(ALL_STORES);
  const [opened, setOpened] = useState(null);
  // Свёрнутые и развёрнутые группы — пока открыта вкладка; смена ресторана или склада — снова по умолчанию
  const [expanded, setExpanded] = useState({});
  // Заголовки групп прилипают под шапкой (десктоп) или под закреплённой панелью фильтров (телефон)
  const [barHeight, setBarHeight] = useState(0);
  const stickyTop = desktop ? DESKTOP_HEADER_HEIGHT : `calc(${MOBILE_TOP_INSET} + ${barHeight}px)`;
  const requestRef = useRef(0); // ответ на устаревший запрос (сменился ресторан) не применяем
  const token = localStorage.getItem('token');
  const key = useMemo(() => cacheKey('stock-negative', token, activeServerName, restaurant), [token, activeServerName, restaurant]);

  const load = useCallback(async () => {
    if (!available || !token) return;
    const request = ++requestRef.current;
    try {
      const result = await fetchStockNegative(token, restaurant);
      if (request !== requestRef.current) return;
      setData(result);
      writeCache(key, result);
      setFailed(false);
    } catch {
      if (request === requestRef.current) setFailed(true);
    }
  }, [available, token, restaurant, key]);

  // Смена ресторана или возврат на вкладку: прежний ответ (если свежий) — сразу, свежий подменит на месте
  useEffect(() => {
    setData(readCache(key));
    setFailed(false);
    setOpened(null); // позиции другого ресторана в списке может не быть
    setExpanded({});
    load();
  }, [key, load]);
  useVisiblePolling(Boolean(available), POLL_MS, load);

  const storeId = effectiveStore(data, store);
  const changeStore = (value) => {
    setStore(value);
    setExpanded({});
  };
  const toggleGroup = (groupId, open) => setExpanded((prev) => ({ ...prev, [groupId]: open }));
  const { stores, totals } = useMemo(() => visibleStores(data, storeId), [data, storeId]);
  const options = data?.store_options || [];

  const openItem = (storeEntry, group, product) =>
    setOpened({ ...product, organizationId: storeEntry.organization_id, storeId: storeEntry.id, storeName: storeEntry.name, groupName: group.name });
  const closeItem = useCallback(() => setOpened(null), []);
  const openedId = opened ? `${opened.storeId}:${opened.product_id}` : null;
  // Десктоп: открытая позиция — колонкой справа, список сужается (панель его не перекрывает)
  const aside = desktop && Boolean(opened);

  let body;
  if (failed && !data) {
    body = <Notice>Данные временно недоступны — попробуйте через несколько минут.</Notice>;
  } else if (data?.available === false) {
    body = (
      <Notice>
        {data.reason === 'restaurant_not_connected'
          ? `Ресторан «${selectionTitle(selectedGroup, allGroups || [])}» не подключён к iikoCloud — складских данных нет.`
          : 'В подключении iikoCloud не выбраны организации — обратитесь к администратору.'}
      </Notice>
    );
  } else if (!data) {
    body = <LoadingList />;
  } else if (totals.items === 0) {
    body = <Notice>Минусовых остатков нет</Notice>;
  } else {
    body = (
      <FadeIn>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {stores.map((s) => (
            <StoreBlock key={s.id} store={s} openedId={openedId} onOpen={openItem} expanded={expanded} onToggle={toggleGroup} stickyTop={stickyTop} />
          ))}
        </Box>
      </FadeIn>
    );
  }
  const summary = data && data.available !== false && totals.items > 0 ? <Summary totals={totals} /> : null;

  const filters = data?.available !== false && (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: { md: 'flex-end' }, gap: { xs: 1, md: 1.5 }, minWidth: 0 }}>
      <StoreFilter options={options} value={storeId} onChange={changeStore} />
      <AsOf data={data} />
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#fff', width: '100%' }}>
      <StickyFiltersBar onHeight={setBarHeight}>
        <FiltersTopRow>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, maxWidth: '100%', flexShrink: 0 }}>
            {allGroups && (
              <GroupSelector
                groups={allGroups}
                selectedGroup={selectedGroup || 'Все'}
                onSelectGroup={setSelectedGroup}
                activeServerName={activeServerName}
                disabledGroups={unavailable}
                disabledHint="нет в iikoCloud"
                sx={{ mb: 0 }}
              />
            )}
            {desktop && tabs}
          </Box>
        </FiltersTopRow>
        {!desktop && tabs}
        {filters}
      </StickyFiltersBar>

      {/* Заголовок и итог — над обеими колонками; ниже список и (десктоп, открытая позиция) панель вровень с ним */}
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
        {/* Отчёт пока один — заголовок; со вторым здесь встанет SegmentedTabs по REPORTS */}
        <Typography component="h2" sx={{ fontSize: { xs: '16px', md: '18px' }, fontWeight: 600, color: '#fff' }}>
          {REPORTS[0].label}
        </Typography>
        {summary}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: aside
              ? { md: `minmax(0, 1fr) ${PANEL_WIDTH.md}px`, lg: `minmax(0, 1fr) ${PANEL_WIDTH.lg}px` }
              : 'minmax(0, 1fr)',
            gap: 2,
            alignItems: 'start',
          }}
        >
          <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
            {body}
            <Typography sx={{ fontSize: '12px', color: MUTED_TEXT_COLOR, textAlign: { xs: 'center', md: 'left' } }}>
              Остатки складов iikoCloud, обновляются раз в 20 минут. Сумма — себестоимость минуса.
            </Typography>
          </Box>
          {/* Десктоп — колонка справа (только открытая позиция), телефон — лист снизу */}
          {(aside || !desktop) && <StockItemPanel item={opened} onClose={closeItem} />}
        </Box>
      </Box>
    </Box>
  );
}
